/**
 * Gobernanza de la memoria (seccion 11).
 *
 * Una memoria que se escribe sola y no se puede mirar es una caja negra. Lo
 * que el agente recuerda tiene que poder verse, corregirse y borrarse.
 */
import { queryOptions } from "@tanstack/react-query";

import { request } from "../lib/api-client.ts";
import { API_BASE } from "../lib/api-routes.ts";
import type { paths } from "../lib/api-types.ts";

/**
 * Los alcances que pueden llegar en una respuesta: `user` y `client`.
 *
 * Son los dos que valida hoy el backend (`SUPPORTED_SCOPE_TYPES` en
 * `memory-note/domain/value-objects/scope.ts`; existe un tercero,
 * `program`, en el vocabulario completo, pero todavía no se puede usar).
 */
export type MemoryScope = "user" | "client";

/** Espejo de `IndexEntry` (`memory-finder.port.ts`). */
export interface MemoryEntry {
  readonly slug: string;
  readonly summary: string;
  readonly scopeType: MemoryScope;
  readonly updatedAt: string;
}

/** Espejo de `NoteView` (`memory-finder.port.ts`). */
export interface MemoryNote extends MemoryEntry {
  readonly content: string;
  readonly origin: string;
  readonly sourceConversationId: string | null;
}

/** Espejo de `MemoryIndexResult` (`memory-index.query.ts`). */
export type MemoryIndex =
  paths["/api/ccmas/v1/memory"]["get"]["responses"][200]["content"]["application/json"];

/**
 * El índice completo de la memoria (`get_memory_index`): una tool
 * `internal: true` del catálogo -- la usa el transporte del agente para
 * inyectarla al iniciar cada turno, y este panel para listar antes de abrir
 * una nota. Su ruta es `GET /memory` (`httpRoute: { method: 'GET', path:
 * '/memory' }`).
 *
 * El OpenAPI real marca `limit` como query param obligatorio (sin valor por
 * defecto en la spec, aunque el back interno sí tenga uno de 150); se manda
 * siempre explícito para no depender de que el back acepte su ausencia.
 */
const MEMORY_INDEX_LIMIT = 150;

export const listMemory = (): Promise<MemoryIndex> =>
  request<MemoryIndex>(
    `${API_BASE}/memory?limit=${String(MEMORY_INDEX_LIMIT)}`,
  );

/**
 * `read_memory` SÍ es una tool del catálogo, pero trae VARIAS notas a la vez
 * (`slugs: string[]`, de 1 a 10) porque es la que usa el agente para abrir
 * varias de un golpe; este panel abre una nota concreta, así que se pide con
 * un arreglo de un elemento y se devuelve la primera.
 */
export const readMemoryNote = (
  slug: string,
  userId: string,
): Promise<MemoryNote> =>
  request<readonly MemoryNote[]>(`${API_BASE}/queries/read_memory`, {
    method: "POST",
    body: JSON.stringify({ userId, slugs: [slug] }),
  }).then((notes) => {
    const note = notes[0];
    if (note === undefined) {
      throw new Error(`No se encontró la nota "${slug}".`);
    }
    return note;
  });

export const saveMemoryNote = (
  slug: string,
  userId: string,
  body: { summary: string; content: string; scope: MemoryScope },
): Promise<unknown> =>
  request(`${API_BASE}/commands/remember`, {
    method: "POST",
    body: JSON.stringify({
      userId,
      scopeType: body.scope,
      slug,
      summary: body.summary,
      content: body.content,
      origin: "manual",
    }),
  });

export const deleteMemoryNote = (
  slug: string,
  userId: string,
  scope: MemoryScope,
): Promise<unknown> =>
  request(`${API_BASE}/commands/forget`, {
    method: "POST",
    body: JSON.stringify({ userId, scopeType: scope, slug }),
  });

/** El ÍNDICE de la memoria: una línea por nota, igual que lo ve el agente. */
export const memoryQuery = () =>
  queryOptions({
    queryKey: ["memory"] as const,
    queryFn: listMemory,
  });
